import { Role } from '~/types/auth';
import { ref } from 'vue';
import type { OrgInfoApiResponse } from '~/types';
import { getCachedOrgInfo } from '~/utils/auth/orgInfoCache';

export const menuItems = reactive({
  dashboard: [
    Role.Admin,
    Role.GrantReportManager,
    Role.ExpenseReportManager,
    Role.BudgetReportManager,
    Role.DonorReportManager,
    Role.ExpenseApprover,
    Role.ExpenseAdmin,
    Role.ReadOnly
    // Role.Auditor
  ],
  expenses: [
    Role.Admin,
    Role.ReadOnly,
    Role.DefaultExpenseAdmin,
    Role.DefaultExpenseApprover,
    Role.DefaultExpenseSubmitter,
    Role.ExpenseAdmin,
    Role.ExpenseSubmitter,
    Role.ExpenseApprover,
    Role.PayrollExpenseAdmin,
    Role.PayrollExpenseApprover,
    Role.PayrollExpenseSubmitter,
    Role.BudgetApprover,
    Role.Auditor
  ],
  recurring_expenses: [
    Role.Admin,
    Role.ReadOnly,
    Role.DefaultExpenseAdmin,
    Role.DefaultExpenseApprover,
    Role.DefaultExpenseSubmitter,
    Role.ExpenseSubmitter,
    Role.ExpenseApprover,
    Role.BudgetApprover
  ],
  advances: [
    Role.Admin,
    Role.ReadOnly,
    Role.ExpenseSubmitter,
    Role.ExpenseApprover,
    Role.AdvanceAdmin,
    Role.AdvanceApprover,
    Role.AdvanceSubmitter
    // Role.Auditor
  ],
  contacts: [
    Role.Admin,
    Role.DonorManager,
    Role.ReadOnly,
    Role.DonationManager,
    Role.PayeeAdmin,
    Role.PayeeApprover,
    Role.PayeeSubmitter,
    Role.PledgeManager
  ],
  donor: [Role.Admin, Role.DonorManager, Role.ReadOnly, Role.DonationManager, Role.PledgeManager],
  payee: [
    Role.Admin,
    Role.ReadOnly,
    Role.DonationManager,
    Role.VendorAdmin,
    Role.VendorApprover,
    Role.VendorSubmitter,
    Role.PayeeAdmin,
    Role.PayeeApprover,
    Role.PayeeSubmitter
  ],
  donations: [Role.Admin, Role.PledgeManager, Role.DonationManager, Role.ReadOnly, Role.Auditor],
  grants: [
    Role.Admin,
    Role.GrantAdmin,
    Role.DonationManager,
    Role.GrantSubmitter,
    Role.GrantMember,
    Role.ReadOnly,
    Role.Auditor,
    Role.DonorAuditor
  ],
  pledges: [Role.Admin, Role.PledgeManager, Role.DonationManager, Role.ReadOnly, Role.Auditor],
  budgets: [
    Role.Admin,
    Role.ReadOnly,
    Role.BudgetAdmin,
    Role.BudgetApprover,
    Role.BudgetMember,
    Role.BudgetSubmitter,
    Role.Auditor
  ],
  utilities: [Role.Admin, Role.UtilitiesAccess, Role.DatabaseAdmin],
  setup: [Role.Admin],
  admin: [Role.Admin]
});

const orgInfo = ref<OrgInfoApiResponse | null>(null);
export const dynamicRolesReady = ref(false);

export const preloadDynamicRoles = async () => {
  if (dynamicRolesReady.value) return;
  orgInfo.value = await getCachedOrgInfo();

  const categories: any[] = orgInfo.value?.data?.expense_categories_list || [];

  const dynamicRoles: string[] = [];
  for (const category of categories) {
    dynamicRoles.push(
      `${category.value}_expense_submitter`,
      `${category.value}_expense_admin`,
      `${category.value}_expense_approver`
    );
  }

  const targetMenuKeys: Array<keyof typeof menuItems> = ['expenses', 'recurring_expenses'];
  for (const key of targetMenuKeys) {
    const existingRoles = new Set(menuItems[key] || []);
    dynamicRoles.forEach((role: any) => existingRoles.add(role));
    menuItems[key] = Array.from(existingRoles);
  }
  dynamicRolesReady.value = true;
};

// export const canAccess = (item: keyof typeof menuItems) => {
//   const { role, isExpenseEnabled } = useAuth();
//   if (item === 'expenses' || item === 'recurring_expenses') {
//     return (
//       isExpenseEnabled.value &&
//       role.value.some(
//         r => r.endsWith('_expense_submitter') || r.endsWith('_expense_admin') || r.endsWith('_expense_approver')
//       )
//     );
//   }

//   if (item === 'budgets' || item === 'advances') {
//     return isExpenseEnabled.value && menuItems[item].some(requiredRole => role.value.includes(requiredRole));
//   }

//   return menuItems[item].some(requiredRole => role.value.includes(requiredRole));
// };
export const canAccess = (item: keyof typeof menuItems) => {
  const { role, isExpenseEnabled } = useAuth();

  if (item === 'expenses' || item === 'recurring_expenses') {
    if (!isExpenseEnabled.value) return false;

    const hasDynamicExpenseRole = role.value.some(
      r => r.endsWith('_expense_submitter') || r.endsWith('_expense_admin') || r.endsWith('_expense_approver')
    );

    const hasStaticExpenseRole = menuItems.expenses.some(requiredRole => role.value.includes(requiredRole));

    return hasDynamicExpenseRole || hasStaticExpenseRole;
  }
  if (item === 'budgets' || item === 'advances') {
    return isExpenseEnabled.value && menuItems[item].some(requiredRole => role.value.includes(requiredRole));
  }

  return menuItems[item].some(requiredRole => role.value.includes(requiredRole));
};
